/**
 * projectAlternatesFlat — adapts the builder's NESTED alternate model into the
 * FLAT sibling model that QuoteSelectionPanel expects.
 *
 * Builder writes:
 *   line.alternates = [{ id, productLabel, total, cost, materialCost, laborCost, margin, sqft, ... }]
 *
 * Panel expects:
 *   sibling line items sharing `alternate_group_id = parent.id`
 *
 * This adapter projects each nested alternate into a top-level line item with
 * the group_id set, so the panel's grouping logic, mutual exclusion, and
 * running-total computation all work without modification.
 *
 * Also re-exports deriveInitialSelections + quoteHasSelections so both
 * RecordDecisionPage and DocumentReviewSurface can import from one shared
 * location.
 *
 * @module quote-builder/selectionAdapter
 */

import { isOptionLine } from './separateOptions';

/**
 * Project nested alternates into flat sibling line items.
 *
 * @param {Array} lineItems - Raw line items from the wire (may have .alternates[])
 * @returns {Array} Expanded array: original lines + projected alternates as siblings
 */
export function projectAlternatesFlat(lineItems) {
  if (!Array.isArray(lineItems) || lineItems.length === 0) return [];

  const expanded = [];

  for (const li of lineItems) {
    const alts = Array.isArray(li.alternates) ? li.alternates : [];
    const hasAlternates = alts.length > 0;

    // The parent line itself.
    // When it HAS alternates, stamp it as a member of its own group so the
    // panel's mutual-exclusion logic includes it in the radio set (not as a
    // locked base line). The parent is the DEFAULT chosen_line_id.
    expanded.push(hasAlternates ? {
      ...li,
      alternate_group_id: li.id,
      alternateGroupId: li.id,
      alternate_group_label: li.productLabel || li.item_name || li.description || '',
      // Normalise label fields so the panel renders names, not raw ids.
      item_name: li.productLabel || li.item_name || li.description || '',
      is_alternate: true,
      isAlternate: true,
    } : li);

    // Project nested alternates as sibling line items in the same group.
    for (const alt of alts) {
      expanded.push({
        // Carry over the alternate's own fields
        ...alt,
        // Mark as belonging to this parent's group
        alternate_group_id: li.id,
        alternateGroupId: li.id,
        alternate_group_label: li.productLabel || li.item_name || li.description || '',
        // Normalise the total field (alternates use `total`, parents use `lineTotal`)
        lineTotal: alt.total ?? alt.lineTotal ?? alt.line_total ?? 0,
        line_total: alt.total ?? alt.lineTotal ?? alt.line_total ?? 0,
        // Normalise label fields
        item_name: alt.productLabel || alt.item_name || alt.description || alt.id || '',
        // Carry parent's area context for display
        areaId: alt.areaId || li.areaId,
        areaLabel: alt.areaLabel || li.areaLabel,
        // Flag so the panel knows this is an alternate
        is_alternate: true,
        isAlternate: true,
      });
    }
  }

  return expanded;
}

/**
 * Derive initial selections from an expanded (flat) line-item array.
 *
 * Options default to `accepted: false` (opt-in per A2 spec).
 * Alternates default to the PARENT line as `chosen_line_id`.
 *
 * @param {Array} expandedLineItems - Output of projectAlternatesFlat
 * @returns {{ options: Array, alternates: Array }}
 */
export function deriveInitialSelections(expandedLineItems) {
  const options = [];
  const alternates = [];
  const seenGroups = new Set();

  for (const li of expandedLineItems) {
    const lineId = li.id || li.line_id;

    // Options: detected via the canonical isOptionLine helper
    if (isOptionLine(li)) {
      options.push({ line_id: lineId, accepted: false });
      continue;
    }

    // Alternates: group by alternate_group_id
    const groupId = li.alternate_group_id || li.alternateGroupId;
    if (groupId && !seenGroups.has(groupId)) {
      seenGroups.add(groupId);
      // Default chosen = the PARENT (the group_id IS the parent's id)
      alternates.push({ group_id: groupId, chosen_line_id: groupId });
    }
  }

  return { options, alternates };
}

/**
 * Check whether a quote has options or alternates worth selecting.
 *
 * @param {Array} expandedLineItems - Output of projectAlternatesFlat
 * @returns {boolean}
 */
export function quoteHasSelections(expandedLineItems) {
  if (!Array.isArray(expandedLineItems) || expandedLineItems.length === 0) return false;
  return expandedLineItems.some(
    (li) => isOptionLine(li) || li.alternate_group_id || li.alternateGroupId
  );
}
